/**
 * "External URL" tab panel of VideoSelectorModal: URL input + preview card.
 * Purely presentational — state lives in the modal's useVideoUrlPreview hook.
 */
import type { UrlPreviewData } from '../hooks/useVideoUrlPreview';
import { formatDuration } from '../utils/formatDuration';
import type { VideoSelectorModalStyles } from './videoSelectorModalStyles';

interface VideoSelectorExternalUrlTabProps {
  externalUrl: string;
  externalUrlError: string | null;
  urlPreview: UrlPreviewData | null;
  isPreviewLoading: boolean;
  onUrlChange: (value: string) => void;
  onPreview: () => void;
  styles: VideoSelectorModalStyles;
}

export function VideoSelectorExternalUrlTab({
  externalUrl,
  externalUrlError,
  urlPreview,
  isPreviewLoading,
  onUrlChange,
  onPreview,
  styles,
}: VideoSelectorExternalUrlTabProps) {
  return (
    <div style={styles.externalUrlPanel}>
      <p style={styles.externalUrlHint}>
        Paste a YouTube, Vimeo, or direct video URL.
      </p>
      <div style={styles.externalUrlInputRow}>
        <input
          type="url"
          value={externalUrl}
          onChange={(e) => onUrlChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onPreview();
            }
          }}
          placeholder="https://…"
          style={styles.externalUrlInput}
          autoFocus
        />
        <button
          type="button"
          style={styles.previewButton}
          onClick={onPreview}
        >
          Preview
        </button>
      </div>
      {externalUrlError && (
        <p style={styles.externalUrlErrorText}>{externalUrlError}</p>
      )}

      {/* Preview card */}
      {isPreviewLoading && (
        <div style={styles.previewCard}>
          <div style={styles.previewThumbnail}>
            <span style={{ fontSize: '16px' }}>⏳</span>
          </div>
          <div style={styles.previewInfo}>
            <span style={styles.previewMeta}>Loading preview…</span>
          </div>
        </div>
      )}
      {urlPreview && !isPreviewLoading && (
        <div style={styles.previewCard}>
          <div style={styles.previewThumbnail}>
            {urlPreview.thumbnailUrl ? (
              <img
                src={urlPreview.thumbnailUrl}
                alt={urlPreview.title}
                style={styles.previewThumbnailImage}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <polygon points="5,3 19,12 5,21" />
              </svg>
            )}
          </div>
          <div style={styles.previewInfo}>
            <span style={styles.previewTitle}>
              {urlPreview.title}
            </span>
            <span style={styles.previewMeta}>
              {urlPreview.domain}
              {urlPreview.duration !== undefined && (
                <> &middot; {formatDuration(urlPreview.duration)}</>
              )}
              {' '}&middot; Detected: {urlPreview.platform}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
